<template>
  <dx-modal
    id="modal-vitals-chart"
    size="lg"
    title="Evolução de sinais e medidas"
    :value="show"
    @input="close"
  >
    <div class="loading loading-lg" v-if="loading" />

    <div class="empty" v-else-if="available.length === 0">
      <div class="empty-icon">
        <fa-icon :icon="['fal', 'chart-line']" size="3x" />
      </div>
      <p class="empty-title h5">Sem dados suficientes</p>
      <p class="empty-subtitle">
        É preciso ter a mesma medida registrada em pelo menos dois atendimentos.
      </p>
    </div>

    <div class="vitals-chart" v-else>
      <div class="form-group">
        <label class="form-label" for="vitals-metric">Medida</label>
        <select id="vitals-metric" class="form-select" v-model="metric">
          <option v-for="item in available" :key="item.key" :value="item.key">
            {{ item.label }}
          </option>
        </select>
      </div>

      <div class="vitals-chart-canvas">
        <st-line-chart :chart-data="chartData" :options="chartOptions" />
      </div>

      <table class="table table-striped table-hover">
        <thead>
          <tr>
            <th>Data</th>
            <th class="text-right" v-for="serie in selected.series" :key="serie.field">
              {{ serie.label }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, i) in rows" :key="i">
            <td>{{ item.date | date('DD/MM/YYYY') }}</td>
            <td class="text-right" v-for="serie in selected.series" :key="serie.field">
              <span v-if="item[serie.field] !== null">
                {{ item[serie.field] }} {{ selected.unit }}
              </span>
              <span class="text-gray" v-else>—</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <template slot="footer">
      <button class="btn" @click="close">Fechar</button>
    </template>
  </dx-modal>
</template>

<script>
import moment from 'moment';

const COLORS = ['#3f6ad8', '#d92550', '#3ac47d', '#f7b924'];

const METRICS = [
  {
    key: 'bloodPressure',
    label: 'Pressão arterial',
    unit: 'mmHg',
    series: [
      { field: 'systolic', label: 'Sistólica' },
      { field: 'diastolic', label: 'Diastólica' },
    ],
  },
  {
    key: 'weight',
    label: 'Peso',
    unit: 'kg',
    series: [{ field: 'weight', label: 'Peso' }],
  },
  {
    key: 'imc',
    label: 'IMC',
    unit: '',
    series: [{ field: 'imc', label: 'IMC' }],
  },
  {
    key: 'abdominalCircumference',
    label: 'Circunferência abdominal',
    unit: 'cm',
    series: [{ field: 'abdominalCircumference', label: 'C. abdominal' }],
  },
  {
    key: 'heartRate',
    label: 'Frequência cardíaca',
    unit: 'bpm',
    series: [{ field: 'heartRate', label: 'FC' }],
  },
  {
    key: 'respiratoryFrequency',
    label: 'Frequência respiratória',
    unit: 'mpm',
    series: [{ field: 'respiratoryFrequency', label: 'FR' }],
  },
  {
    key: 'temperature',
    label: 'Temperatura',
    unit: 'ºC',
    series: [{ field: 'temperature', label: 'Temperatura' }],
  },
  {
    key: 'saturation',
    label: 'Saturação de O²',
    unit: '%',
    series: [{ field: 'saturation', label: 'SatO²' }],
  },
];

const MIN_POINTS = 2;

export default {
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    patientId: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      loading: false,
      metric: '',
      items: [],
    };
  },
  mounted() {
    this.load();
  },
  computed: {
    available() {
      return METRICS.filter(item => item.series.some(serie => (
        this.items.filter(row => row[serie.field] !== null).length >= MIN_POINTS
      )));
    },
    selected() {
      return this.available.find(item => item.key === this.metric) || this.available[0] || null;
    },
    rows() {
      if (!this.selected) {
        return [];
      }

      return this.items.filter(item => (
        this.selected.series.some(serie => item[serie.field] !== null)
      ));
    },
    chartData() {
      if (!this.selected) {
        return { labels: [], datasets: [] };
      }

      return {
        labels: this.rows.map(item => moment(item.date).format('DD/MM/YY')),
        datasets: this.selected.series.map((serie, i) => ({
          label: serie.label,
          data: this.rows.map(item => item[serie.field]),
          borderColor: COLORS[i % COLORS.length],
          backgroundColor: COLORS[i % COLORS.length],
          fill: false,
          spanGaps: true,
          lineTension: 0,
          pointRadius: 3,
        })),
      };
    },
    chartOptions() {
      const unit = this.selected ? this.selected.unit : '';

      return {
        maintainAspectRatio: false,
        legend: {
          display: this.selected ? this.selected.series.length > 1 : false,
        },
        tooltips: {
          callbacks: {
            label: item => `${item.value}${unit ? ` ${unit}` : ''}`,
          },
        },
        scales: {
          yAxes: [{
            ticks: { beginAtZero: false },
          }],
        },
      };
    },
  },
  methods: {
    load() {
      if (!this.patientId) {
        return Promise.resolve();
      }

      this.loading = true;

      return this.$httpX
        .get(`/patients/${this.patientId}/vitals-history`)
        .then(({ data }) => {
          this.items = (data && data.items) || [];
          this.metric = this.available.length > 0 ? this.available[0].key : '';
        })
        .catch(() => {
          this.items = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
    close() {
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/_variables.scss";

#modal-vitals-chart {
  .vitals-chart-canvas {
    height: 18rem;
    margin-bottom: $layout-spacing-lg;
    overflow: hidden;
    position: relative;

    > div {
      height: 100%;
      position: relative;
      width: 100%;
    }
  }

  .table {
    font-size: $font-size-sm;
  }
}
</style>
